import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FaUser } from "react-icons/fa6";
import "./AddSubVendor.css";
import { IoCloudUploadOutline } from "react-icons/io5";
import UploadField from "../legalDocumentUpload/LegalDocumentUpload";
import config from "../../config";
import SuccessPopup from "../../components/common/SuccessPopup";
import ErrorPopup from "../../components/common/ErrorPopup";
import ConfirmPopup from "../../components/common/ConfirmPopup";
import { RiDeleteBinLine } from "react-icons/ri";
import { LuPencilLine } from "react-icons/lu";
import { GrNotification } from "react-icons/gr";
import { IoIosArrowBack } from "react-icons/io";
import Header from "../../components/common/Header";
import { BANK_LIST } from "../constants/dropdownConstants";



const AddSubVendor = ({ onCancel, selectedSubVendor, fetchSubVendors }) => {
  const isViewMode = !!selectedSubVendor;
  const [isEditMode, setIsEditMode] = useState(false);

  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [subVendorTypes, setSubVendorTypes] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [subVendorCategories, setSubVendorCategories] = useState([]);
  const [paymentSchedules, setPaymentSchedules] = useState([]);
  const [selectedClient, setSelectedClient] = useState(0);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [existingDocs, setExistingDocs] = useState({ gst: null, pan: null, adhaar: null });
  const inputProps =
    isViewMode && !isEditMode
      ? { disabled: true, readOnly: true }
      : {};

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";

    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });

    const clientId = localStorage.getItem("orgId");
    if (clientId) {
      setSelectedClient(clientId);
    }
  }, []);

  // Set country and state when selectedSubVendor data is available
  useEffect(() => {
    if (selectedSubVendor) {
      if (selectedSubVendor.country) {
        setSelectedCountry(selectedSubVendor.country);
      }
      if (selectedSubVendor.state) {
        setSelectedState(selectedSubVendor.state);
      }
    }
  }, [selectedSubVendor]);

  // Fetch Existing Documents for View Mode
  useEffect(() => {
    const fetchDocs = async () => {
      if (selectedSubVendor && userDetails.token) {
        const docs = { gst: null, pan: null, adhaar: null };
        const fields = ["gst", "pan", "adhaar"];

        for (const field of fields) {
          let filePath = selectedSubVendor[field];
          if (filePath) {
            // Ensure the prefix is present
            if (!filePath.startsWith("Sub_Vendor/")) {
              filePath = `Sub_Vendor/${filePath}`;
            }

            try {
              const response = await fetch(
                `${config.API_BASE_URL}${config.VIEW_PRESIGNED_URL_ENDPOINT}?filename=${encodeURIComponent(filePath)}`,
                {
                  headers: {
                    Authorization: `Bearer ${userDetails.token}`,
                  },
                }
              );
              if (response.ok) {
                let viewUrl = await response.text();
                // If it's a JSON-wrapped string, parse it
                if (viewUrl.startsWith('"') && viewUrl.endsWith('"')) {
                  viewUrl = JSON.parse(viewUrl);
                }
                docs[field] = {
                  name: filePath.split("/").pop(),
                  url: viewUrl,
                  isExisting: true,
                  path: filePath
                };
              }
            } catch (err) {
              console.error(`Error fetching pre-signed view URL for ${field}:`, err);
            }
          }
        }
        setExistingDocs(docs);
      }
    };

    fetchDocs();
  }, [selectedSubVendor, userDetails.token]);

  // Fetch SubVendorType
  useEffect(() => {
    if (!selectedClient) return;
    const token = localStorage.getItem("Authorization");

    fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${selectedClient}?configType=SubVendorType`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setSubVendorTypes(data?.data || data);
      })
      .catch((err) => {
        console.error("SubVendorType API Error:", err);
      });
  }, [selectedClient]);

  // Fetch PaymentTerms
  useEffect(() => {
    if (!selectedClient) return;
    const token = localStorage.getItem("Authorization");

    fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${selectedClient}?configType=PaymentTerms`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setPaymentTerms(data?.data || data);
      })
      .catch((err) => {
        console.error("PaymentTerms API Error:", err);
      });
  }, [selectedClient]);

  // Fetch SubVendorCategory
  useEffect(() => {
    if (!selectedClient) return;
    const token = localStorage.getItem("Authorization");

    fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${selectedClient}?configType=SubVendorCategory`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setSubVendorCategories(data?.data || data);
      })
      .catch((err) => {
        console.error("SubVendorCategory API Error:", err);
      });
  }, [selectedClient]);

  // Fetch PaymentSchedule
  useEffect(() => {
    if (!selectedClient) return;
    const token = localStorage.getItem("Authorization");

    fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${selectedClient}?configType=PaymentSchedule`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setPaymentSchedules(data?.data || data);
      })
      .catch((err) => {
        console.error("PaymentSchedule API Error:", err);
      });
  }, [selectedClient]);

  // Fetch Countries
  useEffect(() => {
    const token = localStorage.getItem("Authorization");

    fetch(`${config.API_BASE_URL}${config.COUNTRY_ENDPOINT}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const countriesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setCountries(countriesList);
      })
      .catch((err) => {
        console.error("Country API Error:", err);
      });
  }, []);

  // Fetch States when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setStates([]);
      return;
    }
    const token = localStorage.getItem("Authorization");

    fetch(`${config.API_BASE_URL}${config.STATE_ENDPOINT}?countryId=${selectedCountry}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const statesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setStates(statesList);
      })
      .catch((err) => {
        console.error("States API Error:", err);
      });
  }, [selectedCountry]);

  // Fetch Districts when state changes
  useEffect(() => {
    if (!selectedState) {
      setDistricts([]);
      return;
    }
    const token = localStorage.getItem("Authorization");

    fetch(`${config.API_BASE_URL}${config.DISTRICT_ENDPOINT}?stateId=${selectedState}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const districtsList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setDistricts(districtsList);
      })
      .catch((err) => {
        console.error("Districts API Error:", err);
      });
  }, [selectedState]);


  const validationSchema = Yup.object({
    subVendorName: Yup.string()
      .required("Sub Vendor Name is required.")
      .matches(
        /^[a-zA-Z\s\-']*$/,
        "Sub Vendor Name can only contain alphabets, spaces, hyphens, or apostrophes."
      )
      .max(100, "Sub Vendor Name must not exceed 100 characters.")
      .test(
        "no-leading-trailing-spaces",
        "Leading and trailing spaces are not allowed",
        (value) => value?.trim() === value
      ),
    subVendorEmail: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required."),

    subVendorType: Yup.string().required("Sub Vendor Type is required."),

    subVendorCategory: Yup.string().required(
      "Sub Vendor Category is required."
    ),

    corporateIdentificationNumber: Yup.string()
      .matches(
        /^[a-zA-Z0-9]{21}$/,
        "Corporate Identification Number must be exactly 21 alphanumeric characters with no special characters"
      )
      .length(
        21,
        "Corporate Identification Number must be exactly 21 characters"
      ),

    gstNumber: Yup.string()
      .required("GST Number is required.")
      .matches(
        /^[0-9A-Z]{15}$/,
        "GST Number must be exactly 15 alphanumeric characters"
      )
      .length(15, "GST Number must be exactly 15 characters"),

    aadharNumber: Yup.string()
      .required("Aadhar Number is required.")
      .matches(/^\d{12}$/, "Aadhar Number must be exactly 12 digits")
      .length(12, "Aadhar Number must be exactly 12 digits"),

    address: Yup.string()
      .required("Address line is required.")
      .max(100, "Address must not exceed 100 characters"),

    city: Yup.string()
      .required("City is required.")
      .max(50, "City must not exceed 50 characters"),

    district: Yup.string().required("District is required."),

    state: Yup.string().required("State is required."),

    pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be exactly 6 digits")
      .required("Pincode is required."),

    country: Yup.string().required("Country is required."),

    paymentTerms: Yup.string().required("Payment Terms are required."),

    currency: Yup.string().required("Currency is required."),

    paymentFrequency: Yup.string().required(
      "Payment Frequency is required."
    ),

    chargesPerEmployee: Yup.string()
      .required("Charges Per Employee is required.")
      .matches(
        /^\d+(\.\d{1,2})?$/,
        "Charges Per Employee must be a valid numeric value"
      ),
    bankName: Yup.string()
      .required("Bank Name is required")
      .matches(/^[a-zA-Z\s]+$/, "Bank Name must contain only alphabetic characters")
      .max(50, "Bank Name must not exceed 50  characters"),
    accountHolderName: Yup.string()
      .required("Account Holder Name is required")
      .matches(/^[a-zA-Z\s]+$/, "Account Holder Name must contain only alphabetic characters")
      .max(50, "Account Holder Name must not exceed 50 characters"),
    ifsc: Yup.string()
      .required("IFSC is required.")
      .matches(
        /^[A-Z]{4}0[A-Z0-9]{6}$/,
        "IFSC must be 11 characters (first 4 letters, 5th is 0, last 6 alphanumeric)"
      )
      .length(11, "IFSC must be exactly 11 characters"),
    branch: Yup.string()
      .required("Branch is required")
      .matches(/^[a-zA-Z.\s]+$/, "Branch must contain only alphabetic characters and periods")
      .max(100, "Branch must not exceed 100 characters"),
    primaryContactName: Yup.string()
      .required("Primary Contact Name is required.")
      .max(50, "Primary Contact Name must not exceed 50 characters"),

    upiId: Yup.string()
      .matches(
        /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
        "Please enter a valid UPI ID (e.g., username@bank)"
      ),

    panNumber: Yup.string()
      .required("PAN ID is required.")
      .matches(
        /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        "PAN must be in format: ABCDE1234F (5 letters, 4 digits, 1 letter)"
      )
      .length(10, "PAN Number must be exactly 10 characters"),

    accountNumber: Yup.string()
      .required("Bank Account Number is required.")
      .matches(
        /^\d{9,18}$/,
        "Bank Account Number must be between 9 to 18 digits"
      )
      .min(9, "Bank Account Number must be at least 9 digits")
      .max(18, "Bank Account Number must not exceed 18 digits"),
    emailId: Yup.string()
      .email("Please enter a valid email address")
      .test(
        "no-uppercase",
        "Email should not contain uppercase letters.",
        (value) => !value || value === value.toLowerCase()
      )
      .required("Email ID is required"),
    phoneNumber: Yup.string()
      .matches(/^\+91\s\d{10}$/, "Phone number must be 12 digits.")
      .required("Phone Number is required."),
    gst: Yup.mixed()
      .required("GST document is required.")
      .test("fileSize", "File size is too large (max 5MB)", (value) => {
        if (!value || value.isExisting) return true;
        return value.size <= 5 * 1024 * 1024;
      })
      .test("fileType", "Unsupported file format (JPEG/PDF/PNG only)", (value) => {
        if (!value || value.isExisting) return true;
        return ["image/jpeg", "image/png", "application/pdf", "image/jpg"].includes(value.type);
      }),
    pan: Yup.mixed()
      .required("PAN document is required.")
      .test("fileSize", "File size is too large (max 5MB)", (value) => {
        if (!value || value.isExisting) return true;
        return value.size <= 5 * 1024 * 1024;
      })
      .test("fileType", "Unsupported file format (JPEG/PDF/PNG only)", (value) => {
        if (!value || value.isExisting) return true;
        return ["image/jpeg", "image/png", "application/pdf", "image/jpg"].includes(value.type);
      }),
    adhaar: Yup.mixed()
      .required("Aadhar document is required.")
      .test("fileSize", "File size is too large (max 5MB)", (value) => {
        if (!value || value.isExisting) return true;
        return value.size <= 5 * 1024 * 1024;
      })
      .test("fileType", "Unsupported file format (JPEG/PDF/PNG only)", (value) => {
        if (!value || value.isExisting) return true;
        return ["image/jpeg", "image/png", "application/pdf", "image/jpg"].includes(value.type);
      }),
  });
  const initialValues = selectedSubVendor ? {
    subVendorName: selectedSubVendor.subVendorName || "",
    subVendorEmail: selectedSubVendor.subVendorEmail || "",
    subVendorType: selectedSubVendor.subVendorType || "",
    subVendorCategory: selectedSubVendor.subVendorCategory || "",
    corporateIdentificationNumber: selectedSubVendor.ciNumber || "",
    gstNumber: selectedSubVendor.gstNumber || "",
    aadharNumber: selectedSubVendor.aadharNumber || "",
    address: selectedSubVendor.address || "",
    city: selectedSubVendor.city || "",
    district: selectedSubVendor.district || "",
    state: selectedSubVendor.state || "",
    pincode: selectedSubVendor.postalcode || "",
    country: selectedSubVendor.country || "",
    paymentTerms: selectedSubVendor.paymentTerms || "",
    currency: selectedSubVendor.currency || "",
    paymentFrequency: selectedSubVendor.paymentFrequency || "",
    chargesPerEmployee: selectedSubVendor.changesPerEmployee || "",
    primaryContactName: selectedSubVendor.primaryContactName || "",
    emailId: selectedSubVendor.emailAddress || "",
    branch: selectedSubVendor.branch || "",
    phoneNumber: selectedSubVendor.phoneNumber || "+91 ",
    bankName: selectedSubVendor.bankName || "",
    accountHolderName: selectedSubVendor.accountHolderName || "",
    accountNumber: selectedSubVendor.bankAccountNumber || "", ifsc: selectedSubVendor.ifsc || "",
    upiId: selectedSubVendor.upiId || "",
    panNumber: selectedSubVendor.panNumber || "",
    gst: existingDocs.gst,
    pan: existingDocs.pan,
    adhaar: existingDocs.adhaar,
  } : {
    subVendorName: "",
    subVendorEmail: "",
    subVendorType: "",
    subVendorCategory: "",
    corporateIdentificationNumber: "",
    gstNumber: "",
    aadharNumber: "",
    address: "",
    city: "",
    district: "",
    state: "",
    pincode: "",
    country: "",
    paymentTerms: "",
    currency: "",
    paymentFrequency: "",
    chargesPerEmployee: "",
    primaryContactName: "",
    emailId: "",
    phoneNumber: "+91 ",
    bankName: "",
    accountHolderName: "",
    bankaccountNumber: "",
    branch: "",
    ifsc: "",
    upiId: "",
    panNumber: "",
    gst: null,
    pan: null,
    adhaar: null,
  };

  const uploadFileWithPresignedUrl = async (file) => {
    try {
      const filename = `Sub_Vendor/${file.name}`;
      console.log("Generating pre-signed URL for:", filename);

      // 1. Get Pre-signed URL
      const presignedUrlResponse = await fetch(
        `${config.API_BASE_URL}${config.GENERATE_PRESIGNED_URL_ENDPOINT}?filename=${encodeURIComponent(filename)}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
          },
        }
      );

      if (!presignedUrlResponse.ok) {
        throw new Error("Failed to generate pre-signed URL");
      }

      let uploadUrl = await presignedUrlResponse.text();
      if (uploadUrl.startsWith('"') && uploadUrl.endsWith('"')) {
        uploadUrl = JSON.parse(uploadUrl);
      }

      if (!uploadUrl) {
        throw new Error("No upload URL returned from pre-signed URL API");
      }

      console.log("Uploading file to S3:", uploadUrl);

      // 2. Upload file to S3 via PUT
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to pre-signed URL");
      }

      return filename;
    } catch (error) {
      console.error("Error in uploadFileWithPresignedUrl:", error);
      throw error;
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const token = localStorage.getItem("Authorization");
    const orgId = localStorage.getItem("orgId");

    // Upload files if any
    let gstPath = values.gst?.isExisting ? values.gst.path : "";
    let panPath = values.pan?.isExisting ? values.pan.path : "";
    let adhaarPath = values.adhaar?.isExisting ? values.adhaar.path : "";

    try {
      if (values.gst && !values.gst.isExisting) {
        gstPath = await uploadFileWithPresignedUrl(values.gst);
      }
      if (values.pan && !values.pan.isExisting) {
        panPath = await uploadFileWithPresignedUrl(values.pan);
      }
      if (values.adhaar && !values.adhaar.isExisting) {
        adhaarPath = await uploadFileWithPresignedUrl(values.adhaar);
      }
    } catch (err) {
      console.error("Error uploading files:", err);
      setPopupMessage("Failed to upload documents. Please try again.");
      setShowErrorPopup(true);
      setSubmitting(false);
      return;
    }

    // Add orgKey and clientId to the payload
    const payload = {
      subVendorName: values.subVendorName,
      subVendorEmail: values.subVendorEmail,
      subVendorType: values.subVendorType,
      subVendorCategory: values.subVendorCategory,

      ciNumber: values.corporateIdentificationNumber,
      gstNumber: values.gstNumber,
      aadharNumber: values.aadharNumber,

      address: values.address,
      city: values.city,
      district: values.district,
      state: values.state,
      postalcode: values.pincode,
      country: values.country,

      paymentTerms: values.paymentTerms,
      currency: values.currency,
      paymentFrequency: values.paymentFrequency,
      changesPerEmployee: values.chargesPerEmployee,

      primaryContactName: values.primaryContactName,
      emailAddress: values.emailId,
      phoneNumber: values.phoneNumber,

      bankName: values.bankName,
      accountHolderName: values.accountHolderName,
      bankAccountNumber: values.accountNumber,
      branch: values.branch,
      ifsc: values.ifsc,
      upiId: values.upiId,

      panNumber: values.panNumber,

      gst: gstPath,
      pan: panPath,
      adhaar: adhaarPath,

      orgKey: orgId,
      clientId: selectedClient,
    };
    // Safely detect id for update (handles different response shapes)
    const rawId =
      selectedSubVendor?.subVendorId ??
      selectedSubVendor?.id ??
      selectedSubVendor?.subvendorId ??
      null;

    const isUpdate = !!rawId;
    const endpoint = isUpdate
      ? `${config.API_BASE_URL}${config.SUBVENDOR_DETAIL_ENDPOINT}${rawId}`
      : `${config.API_BASE_URL}${config.ADD_SUBVENDOR_ENDPOINT}`;
    const method = isUpdate ? "PUT" : "POST";

    console.log("Submitting SubVendor", {
      endpoint,
      method,
      isUpdate,
      payload,
      rawId,
    });

    try {
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setPopupMessage(isUpdate ? "Sub Vendor updated successfully!" : "Sub Vendor added successfully!");
        setShowSuccessPopup(true);
        if (!isUpdate) {
          resetForm();
        }
        if (fetchSubVendors) {
          fetchSubVendors();
        }
        setTimeout(() => {
          if (onCancel) {
            onCancel();
          }
        }, 1500);
      } else {
        setPopupMessage(data.message || `Failed to ${isUpdate ? 'update' : 'add'} sub vendor. Please try again.`);
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error(`Error ${isUpdate ? 'updating' : 'adding'} sub vendor:`, error);
      setPopupMessage("An error occurred. Please try again later.");
      setShowErrorPopup(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!selectedSubVendor || !selectedSubVendor.subVendorId) {
      setPopupMessage("No sub vendor selected to delete.");
      setShowErrorPopup(true);
      return;
    }
    setShowConfirmPopup(true);
  };

  const handleDeleteConfirm = async () => {
    setShowConfirmPopup(false);

    if (!selectedSubVendor || !selectedSubVendor.subVendorId) {
      setPopupMessage("No sub vendor selected to delete.");
      setShowErrorPopup(true);
      return;
    }

    const token = localStorage.getItem("Authorization");
    const url = `${config.API_BASE_URL}${config.SUBVENDOR_DETAIL_ENDPOINT}${selectedSubVendor.subVendorId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to delete sub vendor");
      }

      setPopupMessage("Sub vendor deleted successfully!");
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Delete error:", error);
      setPopupMessage("Failed to delete sub vendor. Please try again.");
      setShowErrorPopup(true);
    }
  };

  return (
    <div>
      {/* Header Section */}
      {/* Header Section */}
      <Header
        showBack={true}
        onBack={onCancel}
        title={
          isViewMode
            ? isEditMode
              ? "Edit Sub Vendor"
              : "View Sub Vendor"
            : "Add New Sub Vendor"
        }
        subtitle={
          <>
            All Sub Vendor ›{" "}
            {isViewMode
              ? isEditMode
                ? "Edit Sub Vendor"
                : "View Sub Vendor"
              : "Add New Sub Vendor"}
          </>
        }
      />


      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form className='add-sub-vendor-form'>
            {isViewMode && (
              <div className='client-form-action-bar'>
                <button
                  type='button'
                  className='client-delete-btn'
                  onClick={handleDelete}
                >
                  <span className='client-delete-icon'><RiDeleteBinLine /></span>
                  <span>Delete</span>
                </button>

                <button
                  type='button'
                  className='client-edit-btn'
                  onClick={() => setIsEditMode(!isEditMode)}
                >
                  <span className='client-edit-icon'><LuPencilLine /></span>
                  <span>{isEditMode ? "View" : "Edit"}</span>
                </button>
              </div>
            )}

            {/* Sub Vendor Information Section */}
            <section className={`form-section ${isViewMode && !isEditMode ? "view-mode-section" : ""}`}>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>
                Sub Vendor Information
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Sub Vendor Name <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='subVendorName' {...inputProps} />
                  <ErrorMessage
                    name='subVendorName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Email <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='email' name='subVendorEmail' {...inputProps} />
                  <ErrorMessage
                    name='subVendorEmail'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Sub Vendor Type <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='subVendorType' {...inputProps}>
                    <option value=''>Select</option>
                    {subVendorTypes.map((type, index) => (
                      <option key={index} value={type.id}>
                        {type.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='subVendorType'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Sub Vendor Category
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='subVendorCategory' {...inputProps}>
                    <option value=''>Select</option>
                    {subVendorCategories.map((category, index) => (
                      <option key={index} value={category.id}>
                        {category.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='subVendorCategory'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>Corporate Identification Number ( Optional )</label>
                  <Field type='text' name='corporateIdentificationNumber' {...inputProps} />
                  <ErrorMessage
                    name='corporateIdentificationNumber'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    GST Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='gstNumber'
                    {...inputProps}
                    onChange={(e) => {
                      if (isViewMode && !isEditMode) return;
                      let value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
                      if (value.length > 15) {
                        value = value.slice(0, 15);
                      }
                      setFieldValue('gstNumber', value);
                    }}
                    maxLength={15}
                  />
                  <ErrorMessage
                    name='gstNumber'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Aadhar Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='aadharNumber' {...inputProps} />
                  <ErrorMessage
                    name='aadharNumber'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Address line / Street
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='address' {...inputProps} />
                  <ErrorMessage
                    name='address'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    City <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='city' {...inputProps} />                  <ErrorMessage name='city' component='div' className='error' />
                </div>
                <div className='input-group'>
                  <label>
                    Postal Code <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='pincode' {...inputProps} />
                  <ErrorMessage
                    name='pincode'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Country <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='country' required {...inputProps} onChange={(e) => {
                    const value = e.target.value;
                    setFieldValue('country', value);
                    setSelectedCountry(value);
                    setFieldValue('state', '');
                    setFieldValue('district', '');
                    setStates([]);
                    setDistricts([]);
                  }}>
                    <option value=''>Select</option>
                    {countries.map((country, index) => (
                      <option key={country.id || country.countryId} value={country.id || country.countryId}>
                        {country.countryName || country.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='country'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    State <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='state' required {...inputProps} onChange={(e) => {
                    const value = e.target.value;
                    setFieldValue('state', value);
                    setSelectedState(value);
                    setFieldValue('district', '');
                    setDistricts([]);
                  }}>
                    <option value=''>Select</option>
                    {states.map((s, index) => (
                      <option key={s.id || s.stateId} value={s.id || s.stateId}>
                        {s.countryName || s.stateName || s.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='state'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    District <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='district' {...inputProps}>
                    <option value=''>Select</option>
                    {districts.map((d, index) => (
                      <option key={d.id || d.districtId} value={d.id || d.districtId}>
                        {d.countryName || d.districtName || d.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='district'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Payment Terms Section */}
            <section className={`form-section ${isViewMode && !isEditMode ? "view-mode-section" : ""}`}>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Payment Terms
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Payment Terms <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='paymentTerms' required {...inputProps}>
                    <option value=''>Select</option>
                    {paymentTerms.map((term, index) => (
                      <option key={index} value={term.id}>
                        {term.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='paymentTerms'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Currency <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='currency' required {...inputProps}>
                    <option value=''>Select</option>
                    <option value='INR'>INR - Indian Rupee</option>
                    <option value='USD'>USD - US Dollar</option>
                    <option value='EUR'>EUR - Euro</option>
                    <option value='GBP'>GBP - British Pound</option>
                    <option value='AED'>AED - UAE Dirham</option>
                    <option value='SAR'>SAR - Saudi Riyal</option>
                    <option value='SGD'>SGD - Singapore Dollar</option>
                    <option value='AUD'>AUD - Australian Dollar</option>
                  </Field>
                  <ErrorMessage
                    name='currency'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Payment Frequency
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='paymentFrequency' required {...inputProps}>
                    <option value=''>Select</option>
                    {paymentSchedules.map((schedule, index) => (
                      <option key={index} value={schedule.id}>
                        {schedule.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='paymentFrequency'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Charges Per Employee
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='chargesPerEmployee' {...inputProps} />
                  <ErrorMessage
                    name='chargesPerEmployee'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Contact Information Section */}
            <section className={`form-section ${isViewMode && !isEditMode ? "view-mode-section" : ""}`}>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Contact Information
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Primary Contact Name
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='primaryContactName' {...inputProps} />
                  <ErrorMessage
                    name='primaryContactName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Email ID <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='email'
                    name='emailId'
                    {...inputProps}
                    onChange={(e) => {
                      if (isViewMode && !isEditMode) return;
                      setFieldValue("emailId", e.target.value.toLowerCase());
                    }}
                  />                  <ErrorMessage
                    name='emailId'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Phone Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='phoneNumber'
                    {...inputProps}
                    onChange={(e) => {
                      if (isViewMode && !isEditMode) return;
                      let value = e.target.value;

                      // Automatically add +91 if not present
                      if (!value.startsWith("+91 ")) {
                        value = "+91 " + value.slice(4); // Ensure it starts with +91
                      }

                      // Restrict to 14 characters (including +91)
                      if (value.length > 14) {
                        value = value.slice(0, 14);
                      }

                      setFieldValue("phoneNumber", value); // Update field value
                    }}
                  />
                  <ErrorMessage
                    name='phoneNumber'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Banking Information Section */}
            <section className={`form-section ${isViewMode && !isEditMode ? "view-mode-section" : ""}`}>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Banking Information
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Bank Name <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='bankName' {...inputProps}>
                    <option value=''>Select Bank</option>
                    {BANK_LIST.map((bank, index) => (
                      <option key={index} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='bankName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Bank Account Holder Name
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='accountHolderName' {...inputProps} />

                  <ErrorMessage
                    name='accountHolderName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Bank Account Number
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='accountNumber' {...inputProps} />

                  <ErrorMessage
                    name='accountNumber'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Branch <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='branch' {...inputProps} />

                  <ErrorMessage
                    name='branch'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    IFSC <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='ifsc' {...inputProps} />

                  <ErrorMessage name='ifsc' component='div' className='error' />
                </div>
                <div className='input-group'>
                  <label>
                    UPI ID
                  </label>
                  <Field type='text' name='upiId' {...inputProps} />
                  <ErrorMessage
                    name='upiId'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    PAN ID <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='panNumber'
                    {...inputProps}
                    onChange={(e) => {
                      if (isViewMode && !isEditMode) return;
                      let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                      if (value.length > 10) value = value.slice(0, 10);
                      setFieldValue("panNumber", value);
                    }}
                  />
                  <ErrorMessage
                    name='panNumber'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Legal Documents Section */}
            <section className={`form-section ${isViewMode && !isEditMode ? "view-mode-section" : ""}`}>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Legal Documents Upload
              </h3>
              <div className='upload-section'>
                <UploadField label={<>GST <span className='required-asterisk'>*</span></>} name='gst' disabled={isViewMode && !isEditMode} />
                <UploadField label={<>PAN <span className='required-asterisk'>*</span></>} name='pan' disabled={isViewMode && !isEditMode} />
                <UploadField label={<>Aadhar <span className='required-asterisk'>*</span></>} name='adhaar' disabled={isViewMode && !isEditMode} />
              </div>
            </section>

            {/* Action Buttons Section */}
            <div className='button-container'>
              <button type='button' className='cancel-btn' onClick={onCancel}>
                {isViewMode ? "Back" : "Cancel"}
              </button>

              {(!isViewMode || isEditMode) && (
                <button type='submit' className='save-btn' disabled={isSubmitting}>
                  {isSubmitting
                    ? isViewMode
                      ? "Updating..."
                      : "Adding..."
                    : isViewMode
                      ? "Update"
                      : "Add"}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>

      {showSuccessPopup && (
        <SuccessPopup
          message={popupMessage}
          onClose={() => {
            setShowSuccessPopup(false);
            if (onCancel) onCancel();
          }}
        />
      )}

      {showErrorPopup && (
        <ErrorPopup
          message={popupMessage}
          onClose={() => setShowErrorPopup(false)}
        />
      )}

      {showConfirmPopup && (
        <ConfirmPopup
          message="Are you sure you want to delete this sub vendor?"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowConfirmPopup(false)}
        />
      )}
    </div>
  );
};

export default AddSubVendor;
